import { DestroyRef, inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { RowActionSimple } from '@campaign-portal/components-library';
import { exist } from '@campaign-portal/namespace/common/id';

import { CampaignAction, CampaignsService } from '../../services/campaigns.service';
import { CampaignWizardService } from '../../campaign-wizard/campaign-wizard.service';
import { CampaignUtilsService } from '@shared/services/campaign-utils.service';
import { CampaignEntity } from '@shared/types';

@Injectable()
export class CampaignDetailsActionsService {
	readonly actionCompleted$ = new Subject<void>();

	readonly repeatCompleted$ = new Subject<void>();

	private readonly destroyRef = inject(DestroyRef);
	readonly start: RowActionSimple<CampaignEntity<exist>> = {
		icon: 'icon-play',
		label: 'actions.start',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.update({
					...campaign,
					info: { ...campaign.info, scheduled: new Date().toISOString() },
				})
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.actionCompleted$.next());
		},
		shown: (campaign): boolean => !campaign?.isPaused(),
		disabled: (campaign): boolean => !campaign?.canStart(),
	};
	readonly resume: RowActionSimple<CampaignEntity<exist>> = {
		icon: 'icon-play',
		label: 'actions.resume',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.action(CampaignAction.RESUME, campaign)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.actionCompleted$.next());
		},
		shown: (campaign): boolean => campaign?.canResume() ?? false,
	};
	readonly pause: RowActionSimple<CampaignEntity<exist>> = {
		icon: 'icon-pause-circle',
		label: 'actions.pause',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.action(CampaignAction.PAUSE, campaign)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.actionCompleted$.next());
		},
		disabled: (campaign): boolean => !campaign?.canPause(),
	};
	readonly cancel: RowActionSimple<CampaignEntity<exist>> = {
		icon: 'deprecated/stop',
		label: 'actions.cancel',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.action(CampaignAction.CANCEL, campaign)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.actionCompleted$.next());
		},
		disabled: (campaign): boolean => !campaign?.canCancel(),
	};

	readonly repeat: RowActionSimple<CampaignEntity<exist>> = {
		icon: 'icon-arrow-move-list',
		dynamicLabel: (campaign): string => {
			const channels = campaign?.getChannelTypes() ?? [];
			const isCampaignPreModerated = channels.some((channel) => this.campaignUtils.isPreModeratedChannel(channel));
			return isCampaignPreModerated ? 'actions.sendRequest' : 'actions.repeat';
		},
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.cw.repeat(campaign);
			this.repeatCompleted$.next();
		},
		disabled: (campaign): boolean => !campaign?.canRepeat(),
	};

	readonly deleteActionConfig: Omit<RowActionSimple<CampaignEntity<exist>>, 'action'> = {
		icon: '',
		label: 'actions.delete',
		disabled: (campaign): boolean => !campaign?.canDelete(),
		highlight: true,
		separator: true,
	};

	constructor(
		private readonly campaignsService: CampaignsService,
		private readonly cw: CampaignWizardService,
		private readonly campaignUtils: CampaignUtilsService
	) {}
}
